import { Loader } from '@mantine/core'
import { ORPCError } from '@orpc/client'
import { IconPhotoOff } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ImageZoomModal } from '@/components/ui/ImageZoomModal'
import { useClient } from '@/hooks/useClient'

/**
 * The front of the ID Persona checked for the patient's latest attempt, shown live from Persona and
 * never stored by us — the photos stay with Persona (docs/PERSONA_IDV_PLAN.md §8). The middleware
 * sends it to care coordinators and doctors only, the same staff who get the attempt itself.
 */
export function PersonaIdImage({
  patientId,
  attemptId,
  verified,
}: {
  patientId: string
  /** The latest attempt, whose ID the middleware sends: a new attempt is a new image. */
  attemptId: string
  verified: boolean
}) {
  const client = useClient()
  const image = useQuery({
    queryKey: ['persona-id-image', patientId, attemptId],
    queryFn: () => client.getPersonaIdImage({ id: patientId }),
    // Persona answers the same until a new attempt; a gone image will not come back on retry.
    staleTime: 5 * 60_000,
    retry: false,
  })
  const [url, setUrl] = useState<string | null>(null)
  const [zoomOpen, setZoomOpen] = useState(false)

  useEffect(() => {
    if (!image.data) {
      setUrl(null)
      return
    }
    const objectUrl = URL.createObjectURL(image.data)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [image.data])

  const gone = image.error instanceof ORPCError && image.error.code === 'NOT_FOUND'

  return (
    <div className="mb-4 flex flex-col gap-5 md:flex-row md:items-center">
      <div
        className={`flex h-[120px] w-[200px] shrink-0 items-center justify-center overflow-hidden rounded-lg border border-[#D8E1EC] bg-[#F8FAFC] text-center text-sm text-[#8A98AD] ${url ? 'hover:border-primary cursor-pointer transition' : ''}`}
        role={url ? 'button' : undefined}
        tabIndex={url ? 0 : undefined}
        aria-label={url ? 'Enlarge the ID checked by Persona' : undefined}
        onClick={() => url && setZoomOpen(true)}
        onKeyDown={(event) => {
          if (url && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault()
            setZoomOpen(true)
          }
        }}
      >
        {image.isLoading ? (
          <Loader size="sm" />
        ) : url ? (
          <img src={url} alt="ID checked by Persona" className="h-full w-full object-contain" />
        ) : (
          <div className="flex flex-col items-center gap-2 px-2">
            <IconPhotoOff size={28} />
            <span>
              {gone ? 'No longer available at Persona' : "Couldn't load the ID from Persona"}
            </span>
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-base font-semibold text-slate-900">ID checked by Persona</p>
          {verified ? (
            <span className="rounded-full border border-green-200 bg-green-50 px-3 py-1 text-xs font-medium text-green-700">
              Verified with Persona
            </span>
          ) : null}
        </div>
        <p className="mt-1 text-sm text-[#64748B]">
          Shown from Persona&apos;s records — not stored in BeHuman.
        </p>
      </div>
      <ImageZoomModal
        opened={zoomOpen}
        imageUrl={url}
        title="ID checked by Persona"
        imageAlt="ID checked by Persona"
        onClose={() => setZoomOpen(false)}
      />
    </div>
  )
}
